import { byId, isAr, formatCurrency, formatNumber, attachCopyButton, detectUserCountry, detectUserCurrency } from './calc-common.js';
import Big from 'big.js';

const STORAGE_KEY = 'kazakan_electricity_appliances';

export const ARAB_ELECTRICITY_PRESETS = {
  SA: { tariff: 0.18, currency: 'SAR', presetVal: '0.18' },
  AE: { tariff: 0.23, currency: 'AED', presetVal: '0.23' },
  EG: { tariff: 1.25, currency: 'EGP', presetVal: '1.25' },
  KW: { tariff: 0.002, currency: 'KWD', presetVal: '0.002' },
  QA: { tariff: 0.08, currency: 'QAR', presetVal: '0.08' },
  OM: { tariff: 0.015, currency: 'OMR', presetVal: '0.015' },
  BH: { tariff: 0.003, currency: 'BHD', presetVal: '0.003' },
  JO: { tariff: 0.085, currency: 'JOD', presetVal: '0.085' },
  MA: { tariff: 1.15, currency: 'MAD', presetVal: '1.15' },
  DZ: { tariff: 4.18, currency: 'DZD', presetVal: '4.18' },
  TN: { tariff: 0.218, currency: 'TND', presetVal: '0.218' },
  IQ: { tariff: 20, currency: 'IQD', presetVal: '20' }
};

const DEFAULT_APPLIANCES = [
  { id: 1, name: isAr ? 'مكيف سبليت (18,000 وحدة)' : 'Split AC (18,000 BTU)', watts: 1800, hours: 8, count: 2 },
  { id: 2, name: isAr ? 'ثلاجة منزلية كبيرة' : 'Large Refrigerator', watts: 250, hours: 24, count: 1 },
  { id: 3, name: isAr ? 'شاشة تلفزيون LED' : 'LED Smart TV', watts: 120, hours: 6, count: 2 },
  { id: 4, name: isAr ? 'سخان مياه كهربائي' : 'Water Heater', watts: 1500, hours: 3, count: 1 },
  { id: 5, name: isAr ? 'إضاءة الغرف (LED)' : 'LED Room Lighting', watts: 60, hours: 7, count: 5 }
];

let appliances = [];
let nextId = 10;

export function initElectricityCalculator() {
  const container = byId('electricity-calculator-app');
  if (!container) return;

  loadSavedAppliances();

  const tariffInput = byId('elec-tariff');
  const currencySelect = byId('elec-currency');
  const daysInput = byId('elec-days');
  const presetSelect = byId('elec-tariff-preset');

  // Automatic country & currency detection with Saudi Arabia (SA / SAR) fallback
  const detected = (detectUserCountry() || 'SA').toUpperCase();
  const def = ARAB_ELECTRICITY_PRESETS[detected] || ARAB_ELECTRICITY_PRESETS.SA;
  if (currencySelect) {
    currencySelect.value = def.currency;
  }
  if (tariffInput) {
    tariffInput.value = def.tariff;
  }
  if (presetSelect) {
    presetSelect.value = def.presetVal;
  }

  const addBtn = byId('elec-add-btn');
  if (addBtn) {
    addBtn.addEventListener('click', () => {
      appliances.push({
        id: nextId++,
        name: isAr ? 'جهاز جديد' : 'New Appliance',
        watts: 100,
        hours: 4,
        count: 1
      });
      renderAppliancesTable();
      calculateElectricity();
      saveAppliances();
    });
  }

  const resetBtn = byId('elec-reset-btn');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      appliances = JSON.parse(JSON.stringify(DEFAULT_APPLIANCES));
      renderAppliancesTable();
      calculateElectricity();
      saveAppliances();
    });
  }


  if (presetSelect) {
    presetSelect.addEventListener('change', (e) => {
      const val = parseFloat(e.target.value);
      if (!isNaN(val) && val > 0 && tariffInput) {
        tariffInput.value = val;
        calculateElectricity();
      }
    });
  }

  [tariffInput, currencySelect, daysInput].forEach(inp => {
    if (inp) {
      inp.addEventListener('input', calculateElectricity);
      inp.addEventListener('change', calculateElectricity);
    }
  });

  attachCopyButton(byId('elec-copy-btn'), () => {
    const totalCost = byId('elec-total-cost')?.textContent || '';
    const totalKwh = byId('elec-total-kwh')?.textContent || '';
    return isAr
      ? `استهلاك الكهرباء: ${totalKwh}\nالتكلفة الإجمالية: ${totalCost}`
      : `Electricity Consumption: ${totalKwh}\nTotal Cost: ${totalCost}`;
  });

  const printBtn = byId('elec-print-btn');
  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }

  renderAppliancesTable();
  calculateElectricity();
}

function loadSavedAppliances() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      appliances = JSON.parse(saved);
      if (Array.isArray(appliances) && appliances.length > 0) return;
    }
  } catch (e) {
    console.warn('Could not read saved electricity data', e);
  }
  appliances = JSON.parse(JSON.stringify(DEFAULT_APPLIANCES));
}

function saveAppliances() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(appliances));
  } catch (e) {
    console.warn('Could not persist electricity data', e);
  }
}

function renderAppliancesTable() {
  const tbody = byId('elec-table-body');
  if (!tbody) return;

  tbody.innerHTML = appliances.map((app, index) => {
    return `
      <tr data-id="${app.id}">
        <td>
          <input type="text" class="kz-input kz-input-sm app-name" value="${app.name}" style="width:100%;">
        </td>
        <td>
          <input type="number" min="1" max="10000" class="kz-input kz-input-sm app-watts" value="${app.watts}" style="width:75px; text-align:center;">
        </td>
        <td>
          <input type="number" min="0.1" max="24" step="0.5" class="kz-input kz-input-sm app-hours" value="${app.hours}" style="width:65px; text-align:center;">
        </td>
        <td>
          <input type="number" min="1" max="100" class="kz-input kz-input-sm app-count" value="${app.count}" style="width:55px; text-align:center;">
        </td>
        <td class="app-row-kwh" style="text-align:center; font-weight:600; color:var(--brand-primary); font-size:0.875rem;">
          0 kWh
        </td>
        <td style="text-align:center;">
          <button type="button" class="btn-icon-subtle app-delete" title="${isAr ? 'حذف الجهاز' : 'Delete appliance'}" aria-label="${isAr ? 'حذف الجهاز' : 'Delete appliance'}">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </button>
        </td>
      </tr>
    `;
  }).join('');

  // Event bindings
  tbody.querySelectorAll('.app-name').forEach((inp, i) => {
    inp.addEventListener('input', (e) => {
      appliances[i].name = e.target.value;
      saveAppliances();
    });
  });

  tbody.querySelectorAll('.app-watts').forEach((inp, i) => {
    inp.addEventListener('input', (e) => {
      appliances[i].watts = Math.max(0, parseFloat(e.target.value) || 0);
      calculateElectricity();
      saveAppliances();
    });
  });

  tbody.querySelectorAll('.app-hours').forEach((inp, i) => {
    inp.addEventListener('input', (e) => {
      appliances[i].hours = Math.min(24, Math.max(0, parseFloat(e.target.value) || 0));
      calculateElectricity();
      saveAppliances();
    });
  });

  tbody.querySelectorAll('.app-count').forEach((inp, i) => {
    inp.addEventListener('input', (e) => {
      appliances[i].count = Math.max(1, parseInt(e.target.value, 10) || 1);
      calculateElectricity();
      saveAppliances();
    });
  });

  tbody.querySelectorAll('.app-delete').forEach((btn, i) => {
    btn.addEventListener('click', () => {
      if (appliances.length <= 1) return;
      appliances.splice(i, 1);
      renderAppliancesTable();
      calculateElectricity();
      saveAppliances();
    });
  });
}

function calculateElectricity() {
  const days = Math.max(1, parseInt(byId('elec-days')?.value, 10) || 30);
  const tariff = Math.max(0, parseFloat(byId('elec-tariff')?.value) || 0.18);
  const currency = byId('elec-currency')?.value || 'SAR';

  let totalMonthlyKwh = Big(0);
  const tbody = byId('elec-table-body');
  const rows = tbody ? tbody.querySelectorAll('tr') : [];

  appliances.forEach((app, i) => {
    const dailyKwh = Big(app.watts).times(Big(app.hours)).times(Big(app.count)).div(1000);
    const monthlyKwh = dailyKwh.times(days);
    totalMonthlyKwh = totalMonthlyKwh.plus(monthlyKwh);

    if (rows[i]) {
      const kwhCell = rows[i].querySelector('.app-row-kwh');
      if (kwhCell) {
        kwhCell.textContent = `${monthlyKwh.toFixed(1)} kWh`;
      }
    }
  });

  const totalCost = totalMonthlyKwh.times(Big(tariff));
  const dailyCost = days > 0 ? totalCost.div(days) : Big(0);
  const annualCost = dailyCost.times(365);

  if (byId('elec-total-cost')) {
    byId('elec-total-cost').textContent = formatCurrency(totalCost.toFixed(2), currency);
  }
  if (byId('elec-total-kwh')) {
    byId('elec-total-kwh').textContent = `${formatNumber(totalMonthlyKwh.toFixed(1))} kWh`;
  }
  if (byId('elec-daily-avg-kwh')) {
    const dailyAvg = totalMonthlyKwh.div(days);
    byId('elec-daily-avg-kwh').textContent = `${formatNumber(dailyAvg.toFixed(2))} kWh / ${isAr ? 'يوم' : 'day'}`;
  }
  if (byId('elec-annual-cost')) {
    byId('elec-annual-cost').textContent = formatCurrency(annualCost.toFixed(2), currency);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initElectricityCalculator);
  } else {
    initElectricityCalculator();
  }
}
